// UpcomingMeeting.jsx
// The "Upcoming Meeting" tab. Shows the scheduled meeting card with Join /
// Reschedule / Cancel. Reschedule and Cancel render inline sub-views (matching
// screenshots 6, 8 and 9). Visible to both students and teachers.

import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useAuth } from "../../features/auth/AuthContext"; // <-- adjust path
import { Link, useNavigate, useLocation } from "react-router";
import { toast } from "react-toastify";
import { Tooltip } from "react-tooltip";
import CustomCalendar from "../../pages/home/components/dashboard/student/teacher/CustomCalendar";
import ConfirmationModal from "./ConfirmationModal";
import {
  rescheduleMeeting,
  cancelMeeting,
  getTeacherOptions,
  getBookingSlots,
  requestRescheduleMeeting,
  getTeacherSetup,
  respondToRescheduleRequest,
} from "./meetingService";
import {
  toApiDate,
  toDisplayDate,
  toDisplayDateTime,
  STUDENT_TIMEZONE,
  counterpartyName,
  convertTo12Hour,
} from "./meetingUtils";
import { meetingNoteIcon } from "./meetingIcons";


// Inline reschedule form.
const RescheduleView = ({ meeting, onDiscard, onConfirmed }) => {
  const { token } = useAuth();
  const role = useSelector((s) => s.globalState?.role);
  const isTeacher = role === "Teacher";
  const [date, setDate] = useState(null);
  const [time, setTime] = useState("");
  const [slots, setSlots] = useState([]);
  const [tz, setTz] = useState(STUDENT_TIMEZONE);
  const [offeringId, setOfferingId] = useState(null);
  const [openPicker, setOpenPicker] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [error, setError] = useState("");
  const [teacherId, setTeacherId] = useState(meeting.teacher_id || "");
  const [availability, setAvailability] = useState(null);
  const wrapRef = useRef(null);
  const dateButtonRef = useRef(null);
  const globalState = useSelector((s) => s.globalState);

  // Disable past dates and dates beyond 30 days from today
  const disabledDates = (() => {
    const disabled = [];
    const today = new Date();

    // Disable all past dates (before today) and dates after 30 days
    for (let i = -1; i <= 31; i++) {
      const checkDate = new Date(today);
      checkDate.setDate(checkDate.getDate() + i);

      // Disable past dates (before today) and dates beyond 30-day window
      if (i < 0 || i > 30) {
        disabled.push(checkDate.toISOString().split('T')[0]);
      }
    }
    return disabled;
  })();

  useEffect(() => {
    const onDocClick = (e) => {
      // Only close time picker on outside click (not calendar, which handles itself)
      if (openPicker === "time" && wrapRef.current && !wrapRef.current.contains(e.target))
        setOpenPicker(null);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [openPicker]);

  // Teacher side: fetch own setup; Student side: use meeting data
  useEffect(() => {
    if (!isTeacher) {
      if (meeting.teacher_id) {
        setTeacherId(meeting.teacher_id);
      }
      return;
    }

    // Teacher side: fetch own offerings and availability
    let alive = true;
    (async () => {
      const res = await getTeacherSetup(token);
      if (!alive) return;
      if (res.ok) {
        const offerings = res.data?.offerings || [];
        if (offerings.length > 0) {
          setOfferingId(offerings[0].id);
        }
        if (res.data?.availability) {
          setAvailability(res.data.availability);
        }
      } else {
        setError(res.error);
      }
    })();
    return () => {
      alive = false;
    };
  }, [token, isTeacher, meeting.teacher_id]);

  // When date picked, load time slots.
  useEffect(() => {
    if (!date || !teacherId) return;
    let alive = true;
    (async () => {
      setError("");
      setLoadingSlots(true);
      const res = await getBookingSlots(token, {
        teacherId: teacherId,
        date: toApiDate(date),
        timezone: STUDENT_TIMEZONE,
        for_reschedule: meeting.booking_id || meeting.id,
      });
      if (!alive) return;
      setLoadingSlots(false);
      if (res.ok) {
        // Transform slots API response: extract available times for selected offering
        let formattedSlots = [];
        const data = res.data || {};
        const days = data.days || [];

        days.forEach((day) => {
          (day.slots || []).forEach((slot) => {
            // API already returns correct slots, just add them all
            formattedSlots.push({
              time: slot.local_time,
              label: slot.local_time,
              is_full: false,
              start: slot.start, // ISO string from API (e.g., "2026-08-29T09:00:00-04:00")
            });
          });
        });

        setSlots(formattedSlots);
        if (data.display_timezone) setTz(data.display_timezone);
        setTime((prev) =>
          formattedSlots.some((s) => s.time === prev && !s.is_full) ? prev : ""
        );
      } else {
        setError(res.error);
      }
    })();
    return () => {
      alive = false;
    };
  }, [date, token, teacherId, isTeacher]);

  const canConfirm = date && time && !submitting;

  const handleConfirm = async () => {
    if (!canConfirm) return;
    setSubmitting(true);
    setError("");

    if (isTeacher) {
      // Teacher reschedule: use requestRescheduleMeeting with ISO start string
      const selectedSlot = slots.find((s) => s.time === time);
      const res = await requestRescheduleMeeting(token, meeting.id, {
        start: selectedSlot?.start, // ISO string with timezone offset (e.g., "2026-08-31T15:00:00-04:00")
        reason: "",
      });
      setSubmitting(false);
      if (res.ok) {
        toast.success("Reschedule request sent successfully");
        onConfirmed?.();
      } else {
        setError(res.error);
      }
    } else {
      // Student reschedule: use old rescheduleMeeting
    const res = await rescheduleMeeting(token, meeting.id, {
      date: toApiDate(date),
      time,
      timezone: tz,
    });
    setSubmitting(false);
    if (res.ok) {
      toast.success("Meeting rescheduled successfully");
      onConfirmed?.();
    } else {
      setError(res.error);
    }
    }
  };

  const selectedTimeLabel = slots.find((s) => s.time === time)?.label || "";

  // Check if there's a pending reschedule request
  const hasPendingReschedule = meeting.pending_reschedule && meeting.pending_reschedule.status === 1;

  // Use proposed time if there's pending reschedule, otherwise use current time
  const displayDate = hasPendingReschedule ? meeting.pending_reschedule.proposed_local_date : meeting.local_date;
  const displayStartTime = hasPendingReschedule ? meeting.pending_reschedule.proposed_local_start_time : meeting.local_start_time;
  const displayEndTime = hasPendingReschedule ? meeting.pending_reschedule.proposed_local_end_time : meeting.local_end_time;
  const displayDuration = hasPendingReschedule ? meeting.pending_reschedule.proposed_duration_minutes : meeting.duration_minutes;

  return (
    <div className="bm-subview" ref={wrapRef}>
      <div className="bm-subview-panel">
      <h4 className="bm-subview-title">Reschedule Meeting</h4>
      <div className="bm-meeting-note flex items-center">
        <img src={meetingNoteIcon} className="bm-note-icon" alt="" />
        <div>
            <p>
              {hasPendingReschedule ? (
                <>
                  A request has been made to <strong>reschedule</strong> your{" "}
                  <strong>{meeting.session_type_name || "1-on-1"}</strong> session with{" "}
                  <strong>
                    <Link to={`/student/${meeting.student_id || meeting.id}`} style={{ color: "#0068FF", textDecoration: "underline", cursor: "pointer" }}>
                      {meeting.student_name}
                    </Link>
                  </strong>{" "}
                  to <strong>{displayDate}</strong> from <strong>{convertTo12Hour(displayStartTime)} to {convertTo12Hour(displayEndTime)} ({displayDuration} Min).</strong>
                </>
              ) : (
                <>
                  A <strong>{meeting.session_type_name || "1-on-1"}</strong> session with{" "}
              <strong>
                <Link to={`/student/${meeting.student_id || meeting.id}`} style={{ color: "#0068FF", textDecoration: "underline", cursor: "pointer" }}>
                  {meeting.student_name}
                </Link>
              </strong>{" "}
              has been <strong>scheduled</strong> for{" "}
              <strong>{displayDate}</strong> from <strong>{convertTo12Hour(displayStartTime)} to {convertTo12Hour(displayEndTime)} ({displayDuration} Min).</strong>
                </>
              )}
            </p>
          {(meeting.course_name || meeting.price_amount) && (
            <p>
                <strong>
              {meeting.course_name && ` Course: ${meeting.course_name},`}
              {globalState?.enabled_modules?.includes("marketplace") && (
                meeting.price_amount && ` Price: ${meeting.price_currency_symbol || '$'}${meeting.price_amount}`
            )}
                </strong>
            </p>
          )}
              </div>
      </div>

      <div className="bm-two-col">
        <div className="bm-col">
          <div className="bm-field-label">
            Meeting Date <span className="bm-req">*</span>
          </div>
          <div className="bm-select-wrap">
            <button
              ref={dateButtonRef}
              type="button"
              className={`bm-select ${!date ? "bm-placeholder" : ""}`}
              onClick={() =>
                setOpenPicker((p) => (p === "date" ? null : "date"))
              }
            >
              <span className="bm-select-text">{date ? toDisplayDate(date) : "Select Date"}</span>
              <i className="fa-solid fa-chevron-down bm-select-caret" />
            </button>
            <CustomCalendar
              value={date}
              onChange={(d) => {
                setDate(d);
                setTime("");
                setOpenPicker(null);
              }}
              disabledDates={disabledDates}
              triggerRef={dateButtonRef}
              isOpen={openPicker === "date"}
              onClose={() => setOpenPicker(null)}
            />
          </div>
        </div>

        <div className="bm-col">
          <div className="bm-field-label-row">
            <div className="bm-field-label">
              Meeting Time <span className="bm-req">*</span>
            </div>
          </div>
          <div className="bm-select-wrap">
            <button
              type="button"
              className={`bm-select ${!time ? "bm-placeholder" : ""}`}
              disabled={!date}
              onClick={() =>
                setOpenPicker((p) => (p === "time" ? null : "time"))
              }
            >
              <span className="bm-select-text">{selectedTimeLabel ? convertTo12Hour(selectedTimeLabel) : "Select Time"}</span>
              <i className="fa-solid fa-chevron-down bm-select-caret" />
            </button>
            {openPicker === "time" && (
              <div className="bm-dropdown bm-dropdown-scroll">
                {loadingSlots && (
                  <div className="bm-dropdown-empty">Loading...</div>
                )}
                {!loadingSlots && slots.length === 0 && (
                  <div className="bm-dropdown-empty">No slots</div>
                )}
                {!loadingSlots && slots.map((s) => (
                  <button
                    type="button"
                    key={s.time}
                    className="bm-dropdown-item"
                    disabled={s.is_full}
                    onClick={() => {
                      setTime(s.time);
                      setOpenPicker(null);
                    }}
                  >
                    {convertTo12Hour(s.label)}
                    {s.is_full && <span className="bm-slot-full"> · Full</span>}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {error && <p className="bm-error">{error}</p>}

      <div className="bm-actions">
        <button type="button" className="bm-outline-btn" onClick={onDiscard}>
          Discard
        </button>
        <button
          type="button"
          className={`bm-primary-btn ${!canConfirm ? "bm-btn-disabled" : ""}`}
          disabled={!canConfirm}
          onClick={handleConfirm}
        >
          {submitting ? "Confirming…" : "Confirm"}
        </button>
      </div>
      </div>
    </div>
  );
};

// Suggest alternative time (student counter-proposal in negotiation)
const SuggestTimeView = ({ meeting, onDiscard, onConfirmed }) => {
  const { token } = useAuth();
  const role = useSelector((s) => s.globalState?.role);
  const [date, setDate] = useState(null);
  const [time, setTime] = useState("");
  const [slots, setSlots] = useState([]);
  const [tz, setTz] = useState(STUDENT_TIMEZONE);
  const [openPicker, setOpenPicker] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [error, setError] = useState("");
  const [teacherId, setTeacherId] = useState(meeting.teacher_id || "");
  const [note, setNote] = useState("");
  const [offeringId, setOfferingId] = useState(null);
  const [availability, setAvailability] = useState(null);
  const [availableDates, setAvailableDates] = useState([]);
  const wrapRef = useRef(null);
  const dateButtonRef = useRef(null);
  const globalState = useSelector((s) => s.globalState);

  // Calculate disabled dates: disable all dates except those in availableDates
  const disabledDates = (() => {
    if (availableDates.length === 0) {
      // If no available dates loaded yet, disable everything
      const disabled = [];
      const today = new Date();
      for (let i = -1; i <= 32; i++) {
        const checkDate = new Date(today);
        checkDate.setDate(checkDate.getDate() + i);
        disabled.push(checkDate.toISOString().split('T')[0]);
      }
      return disabled;
    }

    // Disable all dates NOT in the availableDates list
    const disabled = [];
    const today = new Date();
    for (let i = -1; i <= 31; i++) {
      const checkDate = new Date(today);
      checkDate.setDate(checkDate.getDate() + i);
      const dateStr = checkDate.toISOString().split('T')[0];
      if (!availableDates.includes(dateStr)) {
        disabled.push(dateStr);
      }
    }
    return disabled;
  })();

  useEffect(() => {
    const onDocClick = (e) => {
      if (openPicker === "time" && wrapRef.current && !wrapRef.current.contains(e.target))
        setOpenPicker(null);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [openPicker]);

  // Fetch teacher options for rescheduling (with for_reschedule flag)
  useEffect(() => {
    if (!meeting.teacher_id || !meeting.pending_reschedule?.id) return;
    let alive = true;
    (async () => {
      const res = await getTeacherOptions(token, meeting.teacher_id, meeting.booking_id || meeting.id);
      if (!alive) return;
      if (res.ok) {
        if (res.data?.availability) {
          setAvailability(res.data.availability);
        }
        if (res.data?.offerings?.[0]?.id) {
          setOfferingId(res.data.offerings[0].id);
        }
        // Extract available dates from response for calendar
        if (res.data?.available_dates && Array.isArray(res.data.available_dates)) {
          setAvailableDates(res.data.available_dates);
        }
      } else {
        setError(res.error);
      }
    })();
    return () => {
      alive = false;
    };
  }, [token, meeting]);

  // When date picked, load time slots
  useEffect(() => {
    if (!date || !teacherId) return;
    let alive = true;
    (async () => {
      setError("");
      setLoadingSlots(true);
      const res = await getBookingSlots(token, {
        teacherId: teacherId,
        date: toApiDate(date),
        timezone: STUDENT_TIMEZONE,
        for_reschedule: meeting.booking_id || meeting.id,
      });
      if (!alive) return;
      setLoadingSlots(false);
      if (res.ok) {
        let formattedSlots = [];
        const data = res.data || {};
        const days = data.days || [];
        days.forEach((day) => {
          (day.slots || []).forEach((slot) => {
            // For student side, show all slots regardless of offering
            formattedSlots.push({
              time: slot.local_time,
              label: slot.local_time,
              is_full: false,
              start: slot.start,
            });
          });
        });
        setSlots(formattedSlots);
        if (data.display_timezone) setTz(data.display_timezone);
        setTime((prev) =>
          formattedSlots.some((s) => s.time === prev && !s.is_full) ? prev : ""
        );
      } else {
        setError(res.error);
      }
    })();
    return () => {
      alive = false;
    };
  }, [date, token, teacherId]);

  const canConfirm = date && time && !submitting;

  const handleConfirm = async () => {
    if (!canConfirm || !meeting.pending_reschedule) return;
    setSubmitting(true);
    setError("");

    const selectedSlot = slots.find((s) => s.time === time);
    const res = await respondToRescheduleRequest(token, meeting.pending_reschedule.id, {
      action: "suggest",
      date: toApiDate(date),
      time: time,
      timezone: tz,
      note: note,
    });
    setSubmitting(false);
    if (res.ok) {
      onConfirmed?.();
    } else {
      setError(res.error);
    }
  };

  const selectedTimeLabel = slots.find((s) => s.time === time)?.label || "";

  // Get proposed time from pending reschedule
  const proposedDate = meeting.pending_reschedule?.proposed_local_date;
  const proposedStartTime = meeting.pending_reschedule?.proposed_local_start_time;
  const proposedEndTime = meeting.pending_reschedule?.proposed_local_end_time;

  return (
    <div className="bm-subview" ref={wrapRef}>
      <div className="bm-subview-panel">
        <h4 className="bm-subview-title">Suggest Alternative Time</h4>
        <div className="bm-meeting-note flex items-center">
          <img src={meetingNoteIcon} className="bm-note-icon" alt="" />
          <div>
          <p>Propose a different time for your {meeting.session_type_name || "1-on-1"} session with{" "}
            <strong>
              <Link to={`/teacher/${meeting.teacher_id}`} style={{ color: "#0068FF", textDecoration: "underline", cursor: "pointer" }}>
                {meeting.teacher_name || meeting.student_name}
              </Link>
            </strong>. {proposedDate && proposedStartTime && (
               <> The proposed time is <strong>{proposedDate}</strong> from <strong>{convertTo12Hour(proposedStartTime)} to {convertTo12Hour(proposedEndTime)}</strong>.</>
            )}</p>
          </div>
        </div>

        <div className="bm-two-col">
          <div className="bm-col">
            <div className="bm-field-label">
              Meeting Date <span className="bm-req">*</span>
            </div>
            <div className="bm-select-wrap">
              <button
                ref={dateButtonRef}
                type="button"
                className={`bm-select ${!date ? "bm-placeholder" : ""}`}
                onClick={() => setOpenPicker((p) => (p === "date" ? null : "date"))}
              >
                <span className="bm-select-text">{date ? toDisplayDate(date) : "Select Date"}</span>
                <i className="fa-solid fa-chevron-down bm-select-caret" />
              </button>
              <CustomCalendar
                value={date}
                onChange={(d) => {
                  setDate(d);
                  setTime("");
                  setOpenPicker(null);
                }}
                disabledDates={disabledDates}
                triggerRef={dateButtonRef}
                isOpen={openPicker === "date"}
                onClose={() => setOpenPicker(null)}
              />
            </div>
          </div>

          <div className="bm-col">
            <div className="bm-field-label-row">
              <div className="bm-field-label">
                Meeting Time <span className="bm-req">*</span>
              </div>
            </div>
            <div className="bm-select-wrap">
              <button
                type="button"
                className={`bm-select ${!time ? "bm-placeholder" : ""}`}
                disabled={!date}
                onClick={() => setOpenPicker((p) => (p === "time" ? null : "time"))}
              >
                <span className="bm-select-text">{selectedTimeLabel ? convertTo12Hour(selectedTimeLabel) : "Select Time"}</span>
                <i className="fa-solid fa-chevron-down bm-select-caret" />
              </button>
              {openPicker === "time" && (
                <div className="bm-dropdown bm-dropdown-scroll">
                  {loadingSlots && (
                    <div className="bm-dropdown-empty">Loading...</div>
                  )}
                  {!loadingSlots && slots.length === 0 && (
                    <div className="bm-dropdown-empty">No slots</div>
                  )}
                  {!loadingSlots && slots.map((s) => (
                    <button
                      type="button"
                      key={s.time}
                      className="bm-dropdown-item"
                      disabled={s.is_full}
                      onClick={() => {
                        setTime(s.time);
                        setOpenPicker(null);
                      }}
                    >
                      {convertTo12Hour(s.label)}
                      {s.is_full && <span className="bm-slot-full"> · Full</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {error && <p className="bm-error">{error}</p>}

        <div className="bm-actions">
          <button type="button" className="bm-outline-btn" onClick={onDiscard}>
            Discard
          </button>
          <button
            type="button"
            className={`bm-primary-btn ${!canConfirm ? "bm-btn-disabled" : ""}`}
            disabled={!canConfirm}
            onClick={handleConfirm}
          >
            {submitting ? "Suggesting…" : "Suggest Time"}
          </button>
        </div>
      </div>
    </div>
  );
};

// Inline cancel confirmation.
const CancelView = ({ meeting, onDiscard, onCancelled }) => {
  const { token } = useAuth();
  const role = useSelector((s) => s.globalState?.role);
  const globalState = useSelector((s) => s.globalState);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [showConfirm, setShowConfirm] = useState(false);
  const isTeacher = role === "Teacher";

  // Check if there's a pending reschedule request
  const hasPendingReschedule = meeting.pending_reschedule && meeting.pending_reschedule.status === 1;

  // Use proposed time if there's pending reschedule, otherwise use current time
  const displayDate = hasPendingReschedule ? meeting.pending_reschedule.proposed_local_date : meeting.local_date;
  const displayStartTime = hasPendingReschedule ? meeting.pending_reschedule.proposed_local_start_time : meeting.local_start_time;
  const displayEndTime = hasPendingReschedule ? meeting.pending_reschedule.proposed_local_end_time : meeting.local_end_time;
  const displayDuration = hasPendingReschedule ? meeting.pending_reschedule.proposed_duration_minutes : meeting.duration_minutes;

  const handleCancelClick = () => {
    setShowConfirm(true);
  };

  const handleConfirmMeetingCancellation = async () => {
    setShowConfirm(false);
    setSubmitting(true);
    setError("");
    const res = await cancelMeeting(token, meeting.id);
    setSubmitting(false);
    if (res.ok) {
      toast.success("Meeting cancelled successfully");
      onCancelled?.();
    } else {
      setError(res.error);
    }
  };

  return (
    <div className="bm-subview">
      <div className="bm-subview-panel">
        <h4 className="bm-subview-title">Cancel Meeting</h4>
        <div className="bm-cancel-row">
          <div className="bm-meeting-note items-center">
            <img src={meetingNoteIcon} className="bm-note-icon" alt="" />
            <div>
            <p>
              {isTeacher ? (
                // Teacher view - Student name is clickable
                <>
                  {hasPendingReschedule ? (
                    <>
                      A request has been made to <strong>reschedule</strong> your{" "}
                      <strong>{meeting.session_type_name || "1-on-1"} session</strong> with{" "}
                      <strong>
                        <Link to={`/student/${meeting.student_id || meeting.id}`} style={{ color: "#0068FF", textDecoration: "underline", cursor: "pointer" }}>
                          {meeting.student_name}
                        </Link>
                      </strong>{" "}
                      to <strong>{displayDate} from {convertTo12Hour(displayStartTime)} to {convertTo12Hour(displayEndTime)}</strong> ({displayDuration} Min).
                    </>
                  ) : (
                <>
                  A <strong>{meeting.session_type_name || "1-on-1"} session</strong> with{" "}
                  <strong>
                    <Link to={`/student/${meeting.student_id || meeting.id}`} style={{ color: "#0068FF", textDecoration: "underline", cursor: "pointer" }}>
                      {meeting.student_name}
                    </Link>
                  </strong>{" "}
                  has been <strong>scheduled</strong> for{" "}
                  <strong>{displayDate} from {convertTo12Hour(displayStartTime)} to {convertTo12Hour(displayEndTime)} ({displayDuration} Min).</strong>
                    </>
                  )}
                </>
              ) : (
                // Student view - Teacher name is clickable
                <>
                  {hasPendingReschedule ? (
                    <>
                      A request has been made to <strong>reschedule</strong> your meeting with the{" "}
                      <strong>
                        <Link to={`/teacher/${meeting.teacher_id}`} style={{ color: "#0068FF", textDecoration: "underline", cursor: "pointer" }}>
                          {meeting.teacher_name || counterpartyName(meeting, role)}
                        </Link>
                      </strong>{" "}
                      to <strong>{displayDate}, at {convertTo12Hour(displayStartTime)}</strong>.
                    </>
                  ) : (
                <>
                  Meeting with the{" "}
                  <strong>
                    <Link to={`/teacher/${meeting.teacher_id}`} style={{ color: "#0068FF", textDecoration: "underline", cursor: "pointer" }}>
                      {meeting.teacher_name || counterpartyName(meeting, role)}
                    </Link>
                  </strong>{" "}
                  has been <strong>scheduled</strong> for <strong>{displayDate}, at {convertTo12Hour(displayStartTime)}</strong>.
                    </>
                  )}
                </>
              )}
            </p>
              {(meeting.course_name || meeting.price_amount) && (
                <p>
                  <strong>
                    {meeting.course_name && ` Course: ${meeting.course_name},`}
                    {globalState?.enabled_modules?.includes("marketplace") && (
                      meeting.price_amount && ` Price: ${meeting.price_currency_symbol || '$'}${meeting.price_amount}`
                    )}
                  </strong>
                </p>
              )}
            </div>
          </div>
          <div className="bm-actions">
            <button type="button" className="bm-outline-btn" onClick={onDiscard}>
              Discard
            </button>
            <button
              type="button"
              className="bm-danger-btn"
              disabled={submitting}
              onClick={handleCancelClick}
            >
              {submitting ? "Cancelling…" : "Cancel"}
            </button>
          </div>
        </div>
        {error && <p className="bm-error">{error}</p>}

        {/* Confirmation Modal */}
        <ConfirmationModal
          isOpen={showConfirm}
          title="Cancel Meeting?"
          message={
            <>
              Are you sure you want to cancel this <strong>{meeting.session_type_name || "1-on-1"}</strong> session scheduled for <strong>{displayDate}</strong> from <strong>{convertTo12Hour(displayStartTime)} to {convertTo12Hour(displayEndTime)}</strong>?
            </>
          }
          onConfirm={handleConfirmMeetingCancellation}
          onCancel={() => setShowConfirm(false)}
          confirmText="Yes, Cancel"
          cancelText="Keep It"
          isLoading={submitting}
          isDangerous={true}
        />
      </div>
    </div>
  );
};

const UpcomingMeeting = ({ meetings, loading, error, onChanged, highlightMeetingId }) => {
  const { token } = useAuth();
  const role = useSelector((s) => s.globalState?.role);
  const globalState = useSelector((s) => s.globalState);
  const navigate = useNavigate();
  const location = useLocation();
  const [view, setView] = useState({ mode: "list", meeting: null }); // list | reschedule | cancel | suggest-time
  const [copiedId, setCopiedId] = useState(null); // which card's link was just copied
  const [highlightedMeetingId, setHighlightedMeetingId] = useState(null);
  const [confirmModal, setConfirmModal] = useState({
    show: false,
    type: null, // 'decline_reschedule'
    meeting: null,
  });
  const [isMobile, setIsMobile] = useState(window.innerWidth < 560);

  // Handle highlighting when highlightMeetingId prop changes
  useEffect(() => {
    if (highlightMeetingId) {
      setHighlightedMeetingId(highlightMeetingId);
      // Auto-remove highlight after 4 seconds
      const timeout = setTimeout(() => {
        setHighlightedMeetingId(null);
        // Clear the highlightMeetingId from location state to prevent re-highlighting on tab change or refresh
        navigate(location.pathname, { replace: true, state: { activeTab: location.state?.activeTab } });
      }, 4000);
      return () => clearTimeout(timeout);
    }
  }, [highlightMeetingId, navigate, location]);

  useEffect(() => {
    if (!loading && highlightedMeetingId) {
      const el = document.querySelector(`[data-meeting-id="${highlightedMeetingId}"]`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [loading, highlightedMeetingId]);
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 560);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);


  const backToList = () => setView({ mode: "list", meeting: null });

  const afterMutation = () => {
    backToList();
    onChanged?.(); // bumps refreshKey in parent → parent refetches → new props flow down
  };

  const handleJoin = (meeting) => {
    const url = meeting.meet_link || "";
    if (url) {
      window.open(url, "_blank", "noopener");
    } else {
      toast.info("Meeting link will be available at meeting time.");
    }
  };

  // First icon: open the Google Calendar event
  const openCalendar = (meeting) => {
    const url = meeting.calendar_link || meeting.meet_link || "";
    if (url) {
      window.open(url, "_blank", "noopener");
    } else {
      toast.info("Calendar event not available yet.");
    }
  };

  // Second icon: copy the meeting link to the clipboard
  const copyLink = async (meeting) => {
    const url = meeting.meet_link || meeting.join_url || "";
    if (!url) {
      toast.info("Meeting link not available yet.");
      return;
    }
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(meeting.id);
      toast.success("Meeting link copied to clipboard!");
      setTimeout(
        () => setCopiedId((id) => (id === meeting.id ? null : id)),
        1500
      );
    } catch (_) {
      toast.error("Could not copy link. Try manually copying the URL.");
    }
  };

  // Accept reschedule request (for both teacher and student)
  const handleAcceptReschedule = async (meeting) => {
    if (!meeting.pending_reschedule) return;
    try {
      const res = await respondToRescheduleRequest(token, meeting.pending_reschedule.id, {
        action: "accept",
      });
      if (res.ok) {
        toast.success("Reschedule request accepted!");
        afterMutation();
      } else {
        const errorMsg = res.error || "Failed to accept reschedule request";
        toast.error(errorMsg);
      }
    } catch (err) {
      const errorMsg = err.message || "Failed to accept reschedule request";
      toast.error(errorMsg);
    }
  };

  // Open decline confirmation modal
  const handleOpenDeclineConfirm = (meeting) => {
    setConfirmModal({ show: true, type: "decline_reschedule", meeting });
  };

  // Student confirms declining the reschedule request from teacher
  const handleConfirmDecline = async () => {
    const meeting = confirmModal.meeting;
    if (!meeting?.pending_reschedule) return;
    setConfirmModal({ show: false, type: null, meeting: null });
    try {
      const res = await respondToRescheduleRequest(token, meeting.pending_reschedule.id, {
        action: "decline",
      });
      if (res.ok) {
        toast.success("Reschedule request declined. Meeting cancelled.");
        afterMutation();
      } else {
        const errorMsg = res.error || "Failed to decline reschedule request";
        toast.error(errorMsg);
      }
    } catch (err) {
      const errorMsg = err.message || "Failed to decline reschedule request";
      toast.error(errorMsg);
    }
  };

  // Close confirmation modal without action
  const handleCloseConfirmModal = () => {
    setConfirmModal({ show: false, type: null, meeting: null });
  };

  if (loading) return <div className="bm-tab-loading">Loading…</div>;
  // Hide auth errors (401) and server errors (5xx), show as empty state instead
  if (error && !error.toLowerCase().includes("invalid token") && !error.match(/\b5\d{2}\b/))
    return <p className="bm-error">{error}</p>;

  if (view.mode === "reschedule")
    return (
      <RescheduleView
        meeting={view.meeting}
        onDiscard={backToList}
        onConfirmed={afterMutation}
      />
    );

  if (view.mode === "suggest-time")
    return (
      <SuggestTimeView
        meeting={view.meeting}
        onDiscard={backToList}
        onConfirmed={afterMutation}
      />
    );

  if (view.mode === "cancel")
    return (
      <CancelView
        meeting={view.meeting}
        onDiscard={backToList}
        onCancelled={afterMutation}
      />
    );

  if (meetings.length === 0)
    return <div className="bm-tab-empty">No upcoming meetings.</div>;

  return (
    <div className="bm-upcoming border border-[var(--bm-border)] rounded-lg">
      {meetings.map((m) => {
        // Check if this meeting has a pending reschedule request
        const hasPendingReschedule = m.pending_reschedule && m.pending_reschedule.status === 1;

        return (
        <div className={`bm-meeting-card ${highlightedMeetingId === m.id ? "bm-meeting-card-highlighted" : ""}`} key={m.id} data-meeting-id={m.id}>
          <div className={isMobile ? "bm-meeting-card-top-mobile" : "bm-meeting-card-top"}>
            <div className="bm-meeting-note items-center">
              <img src={meetingNoteIcon} className="bm-note-icon" alt="" />
              <div>
              <p>
                {hasPendingReschedule ? (
                  // Pending reschedule request message
                  <>
                    A request has been made to <strong>reschedule</strong> your 1-on-1 session with{" "}
                    <strong>
                      <Link to={role === "Teacher" ? `/student/${m.student_id || m.id}` : `/teacher/${m.teacher_id}`} style={{ color: "#0068FF", textDecoration: "underline", cursor: "pointer" }}>
                        {counterpartyName(m, role)}
                      </Link>
                    </strong>{" "}
                    to <strong>{m.pending_reschedule.proposed_local_date} from {convertTo12Hour(m.pending_reschedule.proposed_local_start_time)} to {convertTo12Hour(m.pending_reschedule.proposed_local_end_time)} ({m.pending_reschedule.proposed_duration_minutes} minutes).</strong>
                  </>
                ) : role === "Teacher" ? (
                  // Teacher view - Student name is clickable
                  <>
                    A <strong>{m.session_type_name || "1-on-1"} session</strong> with{" "}
                    <strong>
                      <Link to={`/student/${m.student_id || m.id}`} style={{ color: "#0068FF", textDecoration: "underline", cursor: "pointer" }}>
                        {m.student_name}
                      </Link>
                    </strong>{" "}
                    has been <strong>scheduled</strong> for{" "}
                    <strong>{m.local_date} from {convertTo12Hour(m.local_start_time)} to {convertTo12Hour(m.local_end_time)} ({m.duration_minutes} Min).</strong>
                  </>
                ) : (
                  // Student view - Teacher name is clickable
                  <>
                    Meeting with the{" "}
                    <strong>
                      <Link to={`/teacher/${m.teacher_id}`} style={{ color: "#0068FF", textDecoration: "underline", cursor: "pointer" }}>
                        {m.teacher_name || counterpartyName(m, role)}
                      </Link>
                    </strong>{" "}
                    has been <strong>scheduled</strong> for <strong>{m.local_date}, at {convertTo12Hour(m.local_start_time)}</strong>.
                  </>
                )}
              </p>
                {(m.course_name || m.price_amount) && (
                  <p>
                    <strong>
                    {role === "Teacher" ? (
                      <>
                        {m.course_name && ` Course: ${m.course_name},`}
                        {globalState?.enabled_modules?.includes("marketplace") && (
                          m.price_amount && ` Price: ${m.price_currency_symbol || '$'}${m.price_amount}`
                        )}
                      </>
                    ) : (
                      <>
                        {m.course_name && ` Course: ${m.course_name},`}
                        {globalState?.enabled_modules?.includes("marketplace") && (
                          m.price_amount && ` Price: ${m.price_currency_symbol || '$'}${m.price_amount}`
                        )}
                      </>
                    )}
                    </strong>
                  </p>
                )}
              </div>
            </div>
            <div className="bm-icon-actions">
              <button
                type="button"
                className="bm-icon-btn"
                title="Open in Google Calendar"
                onClick={() => openCalendar(m)}
              >
                <i className="fa-solid fa-arrow-up-right-from-square" />
              </button>
              <button
                type="button"
                className="bm-icon-btn"
                title="Copy meeting link"
                onClick={() => copyLink(m)}
              >
                <i
                  className={`fa-solid ${
                    copiedId === m.id ? "fa-check" : "fa-link"
                  }`}
                />
              </button>
              {/* delete icon = cancel meeting (same action as before) */}
              <button
                type="button"
                className="bm-icon-btn bm-icon-danger"
                title="Cancel meeting"
                onClick={() => setView({ mode: "cancel", meeting: m })}
              >
                <i className="fa-solid fa-trash-can" />
              </button>
            </div>
          </div>

          <div className="bm-meeting-card-actions">
            {/* Reschedule button (show for teacher when no pending reschedule or when awaiting their response) */}
            {role === "Teacher" && (!hasPendingReschedule || m.pending_reschedule?.awaiting_my_response) && (
              <button
                type="button"
                className="bm-outline-btn bm-reschedule-btn"
                onClick={() => setView({ mode: "reschedule", meeting: m })}
              >
                <i className="fa-solid fa-clock-rotate-left" /> Reschedule
              </button>
            )}

            {/* Pending reschedule response buttons */}
            {hasPendingReschedule && m.pending_reschedule.awaiting_my_response && (
                  <>
                    <button
                      type="button"
                      className="bm-outline-btn"
                      onClick={() => handleOpenDeclineConfirm(m)}
                      disabled={loading}
                    >
                  Decline
                    </button>
                {role !== "Teacher" && (
                    <button
                      type="button"
                      className="bm-outline-btn"
                      onClick={() => setView({ mode: "suggest-time", meeting: m })}
                      disabled={loading}
                    >
                    Suggest Time
                  </button>
                )}
            <button
              type="button"
              className="bm-primary-btn"
              onClick={() => handleAcceptReschedule(m)}
              disabled={loading || !m.pending_reschedule?.can_accept}
              data-tooltip-id="accept-reschedule-tooltip"
            >
              Accept
            </button>
            {!m.pending_reschedule?.can_accept && (
              <Tooltip
                id="accept-reschedule-tooltip"
                content="Cannot accept: the proposed time has passed"
                place="top"
                delayShow={0}
              />
            )}
              </>
            )}

            {/* Join Meeting button */}
            {!hasPendingReschedule && (
            <button
              type="button"
              className="bm-primary-btn bm-join-btn"
              onClick={() => handleJoin(m)}
            >
              <i className="fa-solid fa-video" /> Join Meeting
            </button>
            )}
          </div>
        </div>
        );
      })}

      {/* Decline Reschedule Confirmation Modal */}
      <ConfirmationModal
        isOpen={confirmModal.show && confirmModal.type === "decline_reschedule"}
        title="Cancel Meeting?"
        message={
          <>
            Are you sure you want to decline the reschedule request? This will cancel the meeting scheduled for <strong>{confirmModal.meeting?.pending_reschedule?.proposed_local_date || confirmModal.meeting?.local_date}</strong> from <strong>{convertTo12Hour(confirmModal.meeting?.pending_reschedule?.proposed_local_start_time || confirmModal.meeting?.local_start_time)} to {convertTo12Hour(confirmModal.meeting?.pending_reschedule?.proposed_local_end_time || confirmModal.meeting?.local_end_time)}</strong>.
          </>
        }
        onConfirm={handleConfirmDecline}
        onCancel={handleCloseConfirmModal}
        confirmText="Yes, Cancel"
        cancelText="Keep It"
        isLoading={loading}
        isDangerous={true}
      />
    </div>
  );
};

export default UpcomingMeeting;